import {
  TAddNewMasterModalComponentProps,
  TREQUEST_ADD_NEW_FATIGUE_ALLOWANCES_MASTER,
  TREQUEST_ADD_NEW_MACHINE_MASTER,
  TREQUEST_ADD_NEW_OPERATION_MASTER,
  TREQUEST_ADD_NEW_PART_MASTER,
  TREQUEST_ADD_NEW_PRODUCT_TYPE_MASTER,
  TREQUEST_ADD_NEW_STYLE_MASTER,
  TRESPONSE_GET_ALL_PRODUCT_TYPE_OPTIONS,
} from '@monorepo/types';
import { TLiftedFormModel } from '../../model/form.model';
import { FORM_MODIFIER } from '../../model/form.modifier';
import { PIPELINE_FORM } from '../../model/form.pipeline';

export type TAddNewMasterPayload = {
  PRODUCT_TYPE: TREQUEST_ADD_NEW_PRODUCT_TYPE_MASTER;
  OPERATION: Omit<TREQUEST_ADD_NEW_OPERATION_MASTER, 'productTypeId'> & { productTypeId: [string] };
  PART: Omit<TREQUEST_ADD_NEW_PART_MASTER, 'productTypeId'> & { productTypeId: [string] };
  MACHINE: TREQUEST_ADD_NEW_MACHINE_MASTER;
  STYLE_NO: Omit<TREQUEST_ADD_NEW_STYLE_MASTER, 'productTypeId'> & { productTypeId: [string] };
  FATIGUE_ALLOWANCE: TREQUEST_ADD_NEW_FATIGUE_ALLOWANCES_MASTER;
};

export const ADD_NEW_MASTER_FORM_MODEL = {
  createModel: (props: TAddNewMasterModalComponentProps['inputProps']): TLiftedFormModel =>
    PIPELINE_FORM.sanitize({ inputs: getFormInputs(props), colConfig: 'SINGLE_COL' }),

  onParentSave: (formModel: TLiftedFormModel): TLiftedFormModel => FORM_MODIFIER.markTouchedInputs(formModel),
};

function getDescriptionTypeInput(label: string, placeholder: string): TLiftedFormModel['inputs'][number] {
  return {
    input: { type: 'TEXTAREA', rows: 4, placeholder },
    metadata: { keyName: 'description', label, isRequired: false },
  };
}

function getNameTypeInput(
  label: string,
  placeholder: string,
  keyName: string = 'name',
): TLiftedFormModel['inputs'][number] {
  return {
    input: { type: 'INPUT', subType: 'text', placeholder },
    metadata: { keyName, label, isRequired: true },
  };
}

function getProductTypeInput(
  productTypes: TRESPONSE_GET_ALL_PRODUCT_TYPE_OPTIONS['data'],
): TLiftedFormModel['inputs'][number] {
  return {
    input: {
      type: 'DROPDOWN_WITH_SEARCH',
      options: productTypes.map((p) => ({ value: p.id, displayName: `${p.name} (${p.code})` })),
      isMulti: false,
      placeholder: 'Select Product Type..',
    },
    metadata: { keyName: 'productTypeId', label: 'Product Type', isRequired: true },
  };
}

function getFormInputs(props: TAddNewMasterModalComponentProps['inputProps']): TLiftedFormModel['inputs'] {
  switch (props.masterName) {
    case 'PRODUCT_TYPE':
      return [
        getNameTypeInput('Product Name', 'Enter full product name'),
        getDescriptionTypeInput('Description', 'Enter detailed description...'),
      ];

    case 'OPERATION':
      return [
        getProductTypeInput(props.productTypes),
        getNameTypeInput('Operation Name', 'Enter full operation name'),
        getDescriptionTypeInput('Description', 'Enter Operation description, fabric composition, fit type...'),
      ];

    case 'PART':
      return [
        getProductTypeInput(props.productTypes),
        getNameTypeInput('Parts Name', 'Enter full part name'),
        getDescriptionTypeInput('Description', 'Enter Parts description, fabric composition, fit type...'),
      ];

    case 'MACHINE':
      return [
        getNameTypeInput('Machines Name', 'Enter full machine name'),
        {
          input: { type: 'INPUT', subType: 'number', placeholder: 'Enter allowance %', min: 0, max: 100 },
          metadata: { keyName: 'allowancePercent', label: 'Machine Allowance (%)', isRequired: true },
        },
        getDescriptionTypeInput('Description', 'Enter Machines description, fabric composition, fit type...'),
      ];

    case 'STYLE_NO':
      return [
        getProductTypeInput(props.productTypes),
        getNameTypeInput('Style No. Name', 'Enter full Style no. name', 'styleNo'),
        getDescriptionTypeInput('Description', 'Enter Style No. description, fabric composition, fit type...'),
      ];

    case 'FATIGUE_ALLOWANCE':
      return [
        getNameTypeInput('Fatigue Type', 'Enter name...'),
        {
          input: { type: 'INPUT', subType: 'number', placeholder: 'Enter %', min: 0, max: 100 },
          metadata: { keyName: 'valuePercent', label: 'Allowance (%)', isRequired: true },
        },
      ];
  }
}
